import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, Typography, Chip, IconButton, TextField, InputAdornment, MenuItem, Select, FormControl, InputLabel } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import { useKBTheme } from '../../hooks/useKBTheme';
import { mockTickets } from '../../data/mockData';

export const AnimatedTicketListDemo: React.FC = () => {
  const { mode } = useKBTheme();
  const [tickets, setTickets] = useState<typeof mockTickets>([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showTickets, setShowTickets] = useState(false);

  useEffect(() => {
    // Animate tickets appearing
    const timer = setTimeout(() => {
      setShowTickets(true);
      setTickets(mockTickets.slice(0, 3));
    }, 500);

    // Add more tickets with animation
    const timer2 = setTimeout(() => {
      setTickets(mockTickets.slice(0, 5));
    }, 1500);

    return () => {
      clearTimeout(timer);
      clearTimeout(timer2);
    };
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return '#6366f1';
      case 'in_progress': return '#8b5cf6';
      case 'resolved': return '#10b981';
      case 'closed': return '#64748b';
      default: return '#64748b';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return '#ef4444';
      case 'medium': return '#f59e0b';
      case 'low': return '#10b981';
      default: return '#64748b';
    }
  };

  const filteredTickets = tickets.filter((ticket) => {
    const matchesSearch = ticket.summary.toLowerCase().includes(search.toLowerCase()) ||
                         ticket.description.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || ticket.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', background: mode === 'dark' ? '#0f172a' : '#f8fafc' }}>
      {/* Header */}
      <Box sx={{ p: 3, borderBottom: `1px solid ${mode === 'dark' ? 'rgba(71, 85, 105, 0.4)' : 'rgba(226, 232, 240, 0.8)'}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: mode === 'dark' ? '#fff' : '#0f172a' }}>
            My Support Tickets
          </Typography>
          <IconButton sx={{ bgcolor: 'linear-gradient(135deg, #6366f1, #8b5cf6)', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}>
            <AddIcon sx={{ color: '#fff' }} />
          </IconButton>
        </Box>

        {/* Filters */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search tickets..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              flex: 1,
              minWidth: 200,
              '& .MuiOutlinedInput-root': {
                background: mode === 'dark' ? 'rgba(15, 15, 15, 0.5)' : 'rgba(255, 255, 255, 0.8)',
              },
            }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Status"
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="in_progress">In Progress</MenuItem>
              <MenuItem value="resolved">Resolved</MenuItem>
              <MenuItem value="closed">Closed</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Tickets List */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {filteredTickets.map((ticket, idx) => (
          <Card
            key={ticket.id}
            sx={{
              cursor: 'pointer',
              borderRadius: 3,
              background: mode === 'dark'
                ? 'linear-gradient(135deg, rgba(15, 15, 15, 0.95) 0%, rgba(25, 25, 25, 0.95) 100%)'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.98) 100%)',
              border: `1px solid ${mode === 'dark' ? 'rgba(71, 85, 105, 0.3)' : 'rgba(226, 232, 240, 0.8)'}`,
              transition: 'all 0.3s',
              opacity: showTickets ? 1 : 0,
              transform: showTickets ? 'translateY(0)' : 'translateY(20px)',
              animation: `slideIn 0.4s ease-out ${idx * 0.1}s both`,
              '@keyframes slideIn': {
                '0%': { opacity: 0, transform: 'translateY(20px)' },
                '100%': { opacity: 1, transform: 'translateY(0)' },
              },
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: mode === 'dark'
                  ? '0 20px 40px rgba(0, 0, 0, 0.5)'
                  : '0 20px 40px rgba(99, 102, 241, 0.15)',
                borderColor: mode === 'dark' ? 'rgba(139, 92, 246, 0.5)' : 'rgba(99, 102, 241, 0.3)',
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: `linear-gradient(90deg, ${getStatusColor(ticket.status)} 0%, ${getStatusColor(ticket.status)}80 100%)`,
              },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: mode === 'dark' ? '#fff' : '#0f172a', fontSize: '1rem' }}>
                  {ticket.summary}
                </Typography>
                <Chip
                  label={ticket.status.replace('_', ' ').toUpperCase()}
                  size="small"
                  sx={{
                    bgcolor: getStatusColor(ticket.status),
                    color: '#fff',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                  }}
                />
              </Box>
              <Typography variant="body2" sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : '#64748b', mb: 2 }}>
                {ticket.description.substring(0, 100)}...
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={`Priority: ${ticket.priority}`}
                  size="small"
                  sx={{
                    bgcolor: getPriorityColor(ticket.priority),
                    color: '#fff',
                    fontSize: '0.7rem',
                  }}
                />
                <Chip
                  label={(ticket as any).category || 'General'}
                  size="small"
                  sx={{
                    bgcolor: mode === 'dark' ? 'rgba(71, 85, 105, 0.4)' : 'rgba(241, 245, 249, 0.8)',
                    color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : '#64748b',
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );
};

